import { Button, Form, Select } from 'antd'
import { useContext, useMemo } from 'react'
import { toast } from 'react-toastify'

import api from '@/api'
import Loading from '@/components/Loading'
import ModalEmailPreview from '@/components/Modal/ModalEmailPreview'
import ModalTextMessagePreview from '@/components/Modal/ModalTextMessagePreview'
import { TextEditor } from '@/components/TextEditor'
import useAvailableTeamUsers from '@/hooks/settings/useAvailableTeamUsers'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { useAddReminders } from '@/queries/reminders/useAddReminders'
import { useLoadGlobalReminder } from '@/queries/reminders/useLoadReminders'
import { useUpdateReminders } from '@/queries/reminders/useUpdateReminders'
import { CreateReminderProps } from '@/types/reminders'
import { TeamConfig } from '@/types/team-config'
import { ButtonGroup } from '@/ui/atoms/ButtonGroup/ButtonGroup'
import FormAntd from '@/ui/form/antd/FormAntd'
import { PACKETS_FORMAT_OPTIONS } from '@/utils/constants'
import { parseHTMLToPlainText } from '@/utils/parseHTMLToPlainText'
import { ZentakeContext } from '@/utils/ZentakeContext'

import TabBody from '../TabBody'
import {
  getTextWithoutVariables,
  REMINDER_DAY_OPTIONS,
  textMessageLengthColor,
} from './settingsTabUtils'
import { SettingsTabFormModel, useSettingsTabForm } from './useSettingsTabForm'

export const SettingsTab = () => {
  const { teamConfig, setTeamConfig, teamDetails, updateUserDetails } =
    useContext(ZentakeContext)
  const {
    data: globalReminder,
    isLoading: isListLoading,
    isFetching: isListFetching,
  } = useLoadGlobalReminder()
  const { addReminder } = useAddReminders()
  const { updateReminder } = useUpdateReminders()
  const isReminderLoading = isListLoading || isListFetching

  const { availableTeamUsers, isUsersLoading } = useAvailableTeamUsers()
  const isLoading = isReminderLoading || isUsersLoading

  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()

  const hasEmailMessage = searchParams.get('email_message') === 'open'
  const hasTextMessage = searchParams.get('text_message') === 'open'
  const hasEmailReminder = searchParams.get('email_reminder') === 'open'
  const hasSMSReminder = searchParams.get('sms_reminder') === 'open'

  const { form } = useSettingsTabForm()

  const onFormSubmit = async (values: SettingsTabFormModel) => {
    try {
      const dataToSave = {
        send_email_subject: parseHTMLToPlainText(values.emailSubject || ''),
        send_email_content: values.emailMessage,
        sms_content: parseHTMLToPlainText(values.textMessage || ''),
        notification_users: values.notifySubmits,
        download_packet: values.packetFormat,
      }

      const newTeamConfig = (await api.updateTeamConfig(
        teamDetails?.id,
        dataToSave
      )) as TeamConfig
      await updateUserDetails()
      setTeamConfig(newTeamConfig)

      const reminderData: CreateReminderProps = {
        enabled: values.reminderDays !== '0',
        reminder_days: Number(values.reminderDays),
        reminder_condition: 'after_message_sent',
        email_subject: parseHTMLToPlainText(values.email_subject || ''),
        email_body: values.email_body as CreateReminderProps['email_body'],
        sms_body: parseHTMLToPlainText(values.sms_body || ''),
      }

      if (globalReminder?.id) {
        await updateReminder({
          id: globalReminder.id,
          data: reminderData,
          isGlobal: true,
        })
      } else {
        await addReminder(reminderData)
      }

      toast.success('Settings updated')
    } catch (error) {
      toast.error(
        (error as { message: string })?.message ??
          'Something went wrong, please try again later'
      )
    }
  }

  const initialValues = useMemo(
    () => ({
      emailSubject: teamConfig?.send_email_subject ?? '',
      emailMessage: teamConfig?.send_email_content ?? '',
      textMessage: teamConfig?.sms_content ?? '',
      notifySubmits: teamConfig?.notification_users ?? [],
      packetFormat: teamConfig?.download_packet ?? 'pdf',
      reminderDays: globalReminder?.enabled
        ? String(globalReminder?.reminder_days)
        : '0',
      reminderCondition:
        globalReminder?.reminder_condition ?? 'after_message_sent',
      email_subject: globalReminder?.email_subject,
      email_body: globalReminder?.email_body,
      sms_body: globalReminder?.sms_body,
    }),
    [globalReminder, teamConfig]
  )

  return (
    <TabBody>
      {isLoading ? (
        <Loading className="!h-72" />
      ) : (
        <>
          <h2 className="mb-3 w-full border-b border-gray-200 pb-2 text-2xl font-bold">
            Global Invite
          </h2>
          <FormAntd.Root<SettingsTabFormModel>
            className="relative flex h-full w-full flex-col gap-3"
            form={form}
            initialValues={initialValues}
            layout="vertical"
            onFinish={onFormSubmit}
            validateTrigger={['onBlur']}
          >
            <Form.Item<SettingsTabFormModel>
              className="w-full"
              label="Email subject"
              name="emailSubject"
            >
              <TextEditor
                className="text-sm"
                minHeight={30}
                placeholder="Type your email subject message"
                tools={['variables']}
              />
            </Form.Item>

            <div className="relative w-full">
              <Button
                className="absolute right-0 top-0 z-[1] h-7 px-1"
                onClick={() => updateParams(['email_message', 'open'])}
                type="link"
              >
                Preview
              </Button>
              <Form.Item<SettingsTabFormModel>
                className="w-full"
                label="Email message"
                name="emailMessage"
              >
                <TextEditor
                  className="text-sm"
                  minHeight={140}
                  placeholder="Type your email message"
                />
              </Form.Item>
            </div>

            <div className="relative w-full">
              <Button
                className="absolute right-0 top-0 z-[1] h-7 px-1"
                onClick={() => updateParams(['text_message', 'open'])}
                type="link"
              >
                Preview
              </Button>
              <Form.Item<SettingsTabFormModel>
                shouldUpdate={(prevValues, curValues) =>
                  prevValues.textMessage !== curValues.textMessage
                }
              >
                {(propsForm) => {
                  const textMessageLength = getTextWithoutVariables(
                    (propsForm.getFieldValue('textMessage') as string) || ''
                  ).length
                  return (
                    <Form.Item<SettingsTabFormModel>
                      className="w-full [&_.ant-form-item-tooltip]:px-1 [&_.ant-form-item-tooltip]:text-xs"
                      label="Text message"
                      name="textMessage"
                      tooltip={{
                        title: 'Limit of characters',
                        icon: (
                          <span
                            style={{
                              color: textMessageLengthColor(textMessageLength),
                            }}
                          >
                            {textMessageLength}/160
                          </span>
                        ),
                      }}
                    >
                      <TextEditor
                        className="text-sm"
                        maxLength={160}
                        minHeight={50}
                        placeholder="Type your text message"
                        tools={['variables']}
                      />
                    </Form.Item>
                  )
                }}
              </Form.Item>
            </div>
            <div className="mb-3 rounded border !border-[#e2edf8]/70 bg-[#e2edf832]">
              <div className="flex w-full flex-col gap-1.5 px-2.5 pb-3 pt-2">
                <span className="text-base font-bold text-[#374151]">
                  Form Reminder
                </span>
                <div className="flex w-full items-center gap-4">
                  <Form.Item<SettingsTabFormModel>
                    className="mb-0 [&_label]:mb-0 [&_label]:!font-normal"
                    label="Send reminder"
                    layout="horizontal"
                    name="reminderDays"
                  >
                    <Select className="!w-28" options={REMINDER_DAY_OPTIONS} />
                  </Form.Item>
                  <ButtonGroup.Root className="w-[fit-content] gap-2 !border-blue-100 bg-[#e2edf8]">
                    <ButtonGroup.Item
                      className="h-7 !cursor-default select-none px-2.5 text-gray-900 hover:text-gray-900"
                      isActive={false}
                    >
                      After packet/form was sent
                    </ButtonGroup.Item>
                  </ButtonGroup.Root>
                </div>
              </div>

              <Form.Item<SettingsTabFormModel>
                noStyle
                shouldUpdate={(prevValues, curValues) =>
                  prevValues.reminderDays !== curValues.reminderDays
                }
              >
                {(propsForm) => {
                  const shouldSendReminder =
                    propsForm.getFieldValue('reminderDays') !== '0'

                  return shouldSendReminder ? (
                    <>
                      <h2 className="mb-4 mt-1 w-full border-b border-dashed border-gray-200/80 px-2.5 py-2 text-xl font-bold">
                        Global Reminder
                      </h2>
                      <div className="flex w-full flex-col gap-4 px-3">
                        <Form.Item<SettingsTabFormModel>
                          className="w-full"
                          label="Subject"
                          name="email_subject"
                        >
                          <TextEditor
                            className="bg-white text-sm"
                            minHeight={30}
                            placeholder="Type your subject message"
                            tools={['variables']}
                          />
                        </Form.Item>
                        <div className="relative w-full">
                          <Button
                            className="absolute right-0 top-0 z-[1] h-7 px-1"
                            onClick={() =>
                              updateParams(['email_reminder', 'open'])
                            }
                            type="link"
                          >
                            Preview
                          </Button>
                          <Form.Item<SettingsTabFormModel>
                            className="w-full"
                            label="Message"
                            name="email_body"
                          >
                            <TextEditor
                              className="bg-white text-sm"
                              minHeight={140}
                              placeholder="Type your message"
                            />
                          </Form.Item>
                        </div>

                        <div className="relative w-full">
                          <Button
                            className="absolute right-0 top-0 z-[1] h-7 px-1"
                            onClick={() =>
                              updateParams(['sms_reminder', 'open'])
                            }
                            type="link"
                          >
                            Preview
                          </Button>
                          <Form.Item<SettingsTabFormModel>
                            shouldUpdate={(prevValues, curValues) =>
                              prevValues.sms_body !== curValues.sms_body
                            }
                          >
                            {(propsForm) => {
                              const smsBodyLength = getTextWithoutVariables(
                                (propsForm.getFieldValue(
                                  'sms_body'
                                ) as string) || ''
                              ).length
                              return (
                                <Form.Item<SettingsTabFormModel>
                                  className="w-full [&_.ant-form-item-tooltip]:px-1 [&_.ant-form-item-tooltip]:text-xs"
                                  label="Text message"
                                  name="sms_body"
                                  tooltip={{
                                    title: 'Limit of characters',
                                    icon: (
                                      <span
                                        style={{
                                          color:
                                            textMessageLengthColor(
                                              smsBodyLength
                                            ),
                                        }}
                                      >
                                        {smsBodyLength}/160
                                      </span>
                                    ),
                                  }}
                                >
                                  <TextEditor
                                    className="bg-white text-sm"
                                    maxLength={160}
                                    minHeight={50}
                                    placeholder="Type your text message"
                                    tools={['variables']}
                                  />
                                </Form.Item>
                              )
                            }}
                          </Form.Item>
                        </div>
                      </div>
                    </>
                  ) : null
                }}
              </Form.Item>
            </div>
            <Form.Item<SettingsTabFormModel>
              className="w-full"
              label="Notify each time a client submits a form"
              name="notifySubmits"
            >
              <Select showSearch mode="multiple" options={availableTeamUsers} />
            </Form.Item>
            <Form.Item<SettingsTabFormModel>
              className="w-full"
              label="Packet download format"
              name="packetFormat"
            >
              <Select showSearch options={PACKETS_FORMAT_OPTIONS} />
            </Form.Item>

            <div className="mt-2.5 flex w-full justify-end">
              <Button htmlType="submit" type="primary">
                Save
              </Button>
            </div>
          </FormAntd.Root>
          <ModalEmailPreview
            canDismiss
            isVisible={hasEmailMessage}
            message={form.getFieldValue('emailMessage')}
            onClose={() => updateParams(['email_message', undefined])}
          />
          <ModalTextMessagePreview
            canDismiss
            isVisible={hasTextMessage}
            message={form.getFieldValue('textMessage')}
            onClose={() => updateParams(['text_message', undefined])}
          />
          <ModalEmailPreview
            canDismiss
            isVisible={hasEmailReminder}
            message={form.getFieldValue('email_body')}
            onClose={() => updateParams(['email_reminder', undefined])}
          />
          <ModalTextMessagePreview
            canDismiss
            isVisible={hasSMSReminder}
            message={form.getFieldValue('sms_body')}
            onClose={() => updateParams(['sms_reminder', undefined])}
          />
        </>
      )}
    </TabBody>
  )
}
